import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { offset } from "@floating-ui/dom";
import { faExternalLink, faSparkles } from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { omit } from "lodash-es";

import { Button } from "src/base-components/Button";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowOpen,
} from "src/base-components/FloatingWindow/hooks";
import { Pill } from "src/base-components/Pill";
import { Option, Select } from "src/base-components/Select";
import {
  AvailableCaseAction,
  useAvailableCaseActions,
  useAvailableCaseTasks,
  useGetCaseTemplateAction,
  useGetCaseTemplateTask,
  useIsCaseActionFormValid,
} from "src/cases/caseView/hooks";
import { CaseActionFormValues } from "src/cases/caseView/schema";
import { RunnningCaseAction } from "src/cases/caseView/types";
import { isCaseCopilotEnabled } from "src/cases/copilot/isCaseCopilotEnabled";
import {
  useRetryCaseAction,
  useSubmitCaseAction,
} from "src/cases/queries/cases";
import {
  CaseDeserialized,
  TaskIdExtractor,
} from "src/cases/queries/casesTypes";
import {
  useActionFieldHints,
  useHintedNextActionKey,
} from "src/cases/queries/hints";
import {
  CaseTemplateActionOutput,
  CaseTemplateTaskOutput,
} from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";

const RecommendedBadge: React.FC<{ dataLoc?: string }> = ({ dataLoc }) => (
  <Tooltip delay={250} title="Recommended by Copilot" asChild>
    <Pill size="sm" variant="indigo-light" dataLoc={dataLoc}>
      <Pill.Icon icon={faSparkles} />
    </Pill>
  </Tooltip>
);

export const CaseAction: React.FC<{
  caseData?: CaseDeserialized;
  statatusKey?: string;
  isDisabled?: boolean;
}> = ({ caseData, isDisabled = false }) => {
  const availableCaseActions = useAvailableCaseActions(caseData);
  // Tracks only explicit user selections. undefined = no user interaction yet, null = user deselected
  const [userSelectedKey, setUserSelectedKey] = useState<
    string | null | undefined
  >(undefined);

  const activeAction = caseData?.data?.running_actions?.at(0) as
    | RunnningCaseAction
    | undefined
    | null;

  const hintedNextActionKey = useHintedNextActionKey(caseData?.data.id, {
    enabled: isCaseCopilotEnabled(caseData),
  });

  const selectedActionKey = useMemo(() => {
    // Active server action takes priority
    if (activeAction?.type === "case_action") {
      return activeAction.action_key ?? null;
    }
    // User has explicitly selected or deselected
    if (userSelectedKey !== undefined) {
      return userSelectedKey;
    }
    // AI recommendation as default
    if (
      hintedNextActionKey &&
      availableCaseActions.some(
        (action) => action.action_key === hintedNextActionKey,
      )
    ) {
      return hintedNextActionKey;
    }
    return null;
  }, [
    activeAction,
    userSelectedKey,
    hintedNextActionKey,
    availableCaseActions,
  ]);

  // When there's an active action and user hasn't explicitly selected anything,
  // adopt the active action's key as the user selection so it persists after dismiss
  useEffect(() => {
    if (activeAction?.type === "case_action" && userSelectedKey === undefined) {
      setUserSelectedKey(activeAction.action_key ?? null);
    }
  }, [activeAction, userSelectedKey]);

  const selectedAction = useMemo(
    () =>
      availableCaseActions.find(
        (action) => action.action_key === selectedActionKey,
      ),
    [availableCaseActions, selectedActionKey],
  );

  return (
    <CaseActionInner
      key={selectedAction?.action_key}
      activeAction={activeAction}
      availableCaseActions={availableCaseActions}
      caseData={caseData}
      hintedNextActionKey={hintedNextActionKey}
      isDisabled={isDisabled}
      selectedAction={selectedAction}
      onSelectedActionChange={setUserSelectedKey}
    />
  );
};

type CaseActionInnerProps = {
  /** Case data for which actions are being performed */
  caseData?: CaseDeserialized;
  /** Available case actions for the case */
  availableCaseActions: AvailableCaseAction[];
  /** Selected action on the client side */
  selectedAction?: AvailableCaseAction;
  /** BE persisted active action on the case */
  activeAction?: RunnningCaseAction | null;
  /** Whether the actions should be disabled */
  isDisabled?: boolean;
  /** The action key recommended by AI copilot */
  hintedNextActionKey?: string | null;
  onSelectedActionChange: (key: string | null | undefined) => void;
};

export const CaseActionInner: React.FC<CaseActionInnerProps> = ({
  availableCaseActions,
  caseData,
  onSelectedActionChange,
  selectedAction,
  activeAction,
  hintedNextActionKey,
  isDisabled = false,
}) => {
  const getCaseTemplateAction = useGetCaseTemplateAction(caseData);

  const { data: fieldHints } = useActionFieldHints(
    caseData?.data.id,
    selectedAction?.id,
    {
      enabled: isCaseCopilotEnabled(caseData),
    },
  );
  const hasHints = fieldHints != null && Object.keys(fieldHints).length > 0;

  const formWindowTargetRef = useRef<HTMLDivElement>(null);
  const { open } = useFloatingWindowsActions();
  const isFormWindowOpen = useIsFloatingWindowOpen(
    getFormWindowId(selectedAction?.action_key ?? ""),
  );

  const actionsInCaseTemplate = useMemo(() => {
    return availableCaseActions
      .map((action) => getCaseTemplateAction(action.action_key))
      .filter(isCaseTemplateAction);
  }, [availableCaseActions, getCaseTemplateAction]);

  const selectedActionInTemplate = getCaseTemplateAction(
    selectedAction?.action_key,
  );

  const handleOpenFormWindow = useCallback(() => {
    if (!selectedAction || isDisabled) return;

    open(
      {
        id: getFormWindowId(selectedAction.action_key),
        type: "cm-action",
        target: formWindowTargetRef,
        positionOptions: {
          placement: "left-start",
          middleware: [offset({ mainAxis: 27, crossAxis: 0 })],
        },
      },
      {
        selectedAction,
        actionInTemplate: selectedActionInTemplate,
        disabled: isDisabled || isFormWindowOpen,
        caseId: caseData?.data.id,
      },
    );
  }, [
    open,
    caseData?.data.id,
    isFormWindowOpen,
    selectedAction,
    selectedActionInTemplate,
    isDisabled,
  ]);

  return (
    <>
      <CaseActionSelect
        caseActions={actionsInCaseTemplate}
        caseData={caseData}
        disabled={isDisabled || isFormWindowOpen || !!activeAction}
        hintedNextActionKey={hintedNextActionKey}
        selectedKey={selectedAction?.action_key ?? null}
        onSelect={(key) => {
          onSelectedActionChange(key);
        }}
      />

      {selectedAction?.form && (
        <div ref={formWindowTargetRef}>
          <Button
            dataLoc="case-action-form-window-trigger"
            disabled={isDisabled || !!activeAction || isFormWindowOpen}
            iconRight={hasHints ? faSparkles : faExternalLink}
            variant="secondary"
            fullWidth
            onClick={handleOpenFormWindow}
          >
            Fill form
          </Button>
        </div>
      )}

      <CaseActionSubmitButton
        activeAction={activeAction}
        caseEtag={caseData?.data.etag}
        caseId={caseData?.data.id}
        disabled={isDisabled || isFormWindowOpen}
        hasHints={hasHints}
        selectedAction={selectedAction}
        selectedActionInTemplate={selectedActionInTemplate}
        onSuccess={() => onSelectedActionChange(undefined)}
      />
    </>
  );
};

const CaseActionSubmitButton: React.FC<{
  disabled?: boolean;
  hasHints?: boolean;
  activeAction?: RunnningCaseAction | null;
  selectedAction?: AvailableCaseAction;
  selectedActionInTemplate?: CaseTemplateActionOutput;
  caseEtag?: string;
  caseId?: string;
  onSuccess?: () => void;
}> = ({
  disabled,
  hasHints,
  caseId,
  caseEtag,
  activeAction,
  selectedAction,
  selectedActionInTemplate,
  onSuccess,
}) => {
  const isValid = useIsCaseActionFormValid(
    selectedActionInTemplate,
    extracActionFormValues(selectedAction),
  );

  const { mutateAsync: submitCaseAction, isPending: isSubmittingCaseAction } =
    useSubmitCaseAction(caseId);
  const { mutateAsync: retryCaseAction, isPending: isRetryingCaseAction } =
    useRetryCaseAction(caseId);

  // If there's an active case action and it's a failed case action, we can retry it
  const canRetryAction =
    !!activeAction &&
    activeAction?.type === "case_action" &&
    activeAction?.status === "failed";

  const hasCaseQueuedAction =
    !!activeAction &&
    activeAction?.type === "case_action" &&
    activeAction?.status === "queued";

  const handleSubmitCaseAction = useCallback(async () => {
    try {
      await submitCaseAction({
        etag: caseEtag ?? "0",
        formEtag: selectedAction?.form?.meta?.etag,
        actionId: selectedAction?.id ?? "",
      });

      toastActions.success({
        title: "Action submitted successfully",
      });
      onSuccess?.();
    } catch (error) {
      logger.error("Error submitting case action form:", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to submit action",
          description:
            "The action or case has been modified elsewhere. Please reload and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to submit action",
          description: error.response?.data?.message,
        });
        return;
      }

      toastActions.failure({
        title: "Failed to submit action",
        description: "An unexpected error occurred. Please try again.",
      });
    }
  }, [selectedAction, caseEtag, submitCaseAction, onSuccess]);

  const handleRetryCaseAction = useCallback(async () => {
    if (!activeAction?.id) return;

    try {
      await retryCaseAction({
        actionId: activeAction.id,
        etag: caseEtag ?? 0,
      });

      toastActions.success({
        title: "Action retried",
      });
      onSuccess?.();
    } catch (error) {
      logger.error("Error retrying case action:", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to retry action",
          description:
            "The action or case has been modified elsewhere. Please reload and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to retry action",
          description: error.response?.data?.message,
        });
        return;
      }

      toastActions.failure({
        title: "Failed to retry action",
        description: "An unexpected error occurred. Please try again.",
      });
    }
  }, [activeAction, retryCaseAction, caseEtag, onSuccess]);

  const handleSubmit = useCallback(async () => {
    if (canRetryAction) {
      await handleRetryCaseAction();
    } else {
      await handleSubmitCaseAction();
    }
  }, [handleRetryCaseAction, handleSubmitCaseAction, canRetryAction]);

  const isLoading =
    isSubmittingCaseAction || isRetryingCaseAction || hasCaseQueuedAction;
  // Only if the form is valid and either there's no active action or active action is retryable (in failed state)
  const canSubmitAction =
    isValid && (!activeAction || canRetryAction) && !hasHints;

  return (
    <Tooltip
      dataLoc="case-action-submit-button-tooltip"
      delay={300}
      disabled={canSubmitAction || hasCaseQueuedAction}
      title={
        hasHints && !isValid
          ? "Review suggestions and fill required fields before submitting"
          : hasHints
            ? "Review all Copilot suggestions before submitting"
            : "Form needs to be filled to submit this action"
      }
      asChild
    >
      <Button
        dataLoc="case-action-submit-button"
        disabled={disabled || isLoading || !canSubmitAction}
        loading={isLoading}
        fullWidth
        onClick={handleSubmit}
      >
        Submit
      </Button>
    </Tooltip>
  );
};

const getIncompleteRequiredTasks = (
  actionKey: string,
  caseData: CaseDeserialized,
  availableTasks: ReturnType<typeof useAvailableCaseTasks>,
  getTemplateTask: ReturnType<typeof useGetCaseTemplateTask>,
): CaseTemplateTaskOutput[] => {
  const { case_properties, template_version } = caseData.data;
  const { status, sub_status } = case_properties ?? {};
  const workflowRules = template_version?.workflow_rules;

  if (!workflowRules) return [];

  const currentWorkflowRule = Object.values(workflowRules).find(
    (rule) =>
      rule.status === status &&
      (rule.sub_status ?? null) === (sub_status ?? null),
  );

  if (!currentWorkflowRule) return [];

  const actionConfig = currentWorkflowRule.actions.find(
    (action) => action.action_key === actionKey,
  );

  if (!actionConfig?.required_tasks_keys) return [];

  return actionConfig.required_tasks_keys.reduce<CaseTemplateTaskOutput[]>(
    (incompleteTasks, { task_key }) => {
      const availableTask = availableTasks.find(
        (task) => TaskIdExtractor.getTaskKey(task.id) === task_key,
      );
      const templateTask = getTemplateTask(task_key);

      if (availableTask && !availableTask.is_completed && templateTask) {
        incompleteTasks.push(templateTask);
      }

      return incompleteTasks;
    },
    [],
  );
};

const ActionDisabledTooltip: React.FC<{
  tasks: CaseTemplateTaskOutput[];
}> = ({ tasks }) => (
  <div className="flex w-70 flex-col gap-2">
    <div className="font-inter-semibold-12px">
      Tasks listed below must be completed to perform this action:
    </div>
    <div className="flex flex-col gap-1">
      {tasks.map((task) => (
        <div key={task.key} className="w-fit">
          <Pill size="sm" variant="dark-gray">
            <Pill.Text>{task.display_name}</Pill.Text>
          </Pill>
        </div>
      ))}
    </div>
  </div>
);

const CaseActionSelect: React.FC<{
  disabled?: boolean;
  selectedKey: Nullable<string>;
  onSelect: (key: Nullable<string> | undefined) => void;
  caseActions: CaseTemplateActionOutput[];
  caseData?: CaseDeserialized;
  hintedNextActionKey?: string | null;
}> = ({
  selectedKey,
  onSelect,
  disabled,
  caseActions,
  caseData,
  hintedNextActionKey,
}) => {
  const availableTasks = useAvailableCaseTasks(caseData);
  const getTemplateTask = useGetCaseTemplateTask(caseData);

  const caseActionOptions = useMemo(() => {
    if (!caseData) return [];

    return caseActions.map((action) => {
      const incompleteTasks = getIncompleteRequiredTasks(
        action.key,
        caseData,
        availableTasks,
        getTemplateTask,
      );

      return {
        key: action.key,
        value:
          hintedNextActionKey === action.key ? (
            <span className="flex w-full items-center justify-between gap-1">
              <span className="truncate">{action.display_name}</span>
              <RecommendedBadge />
            </span>
          ) : (
            action.display_name
          ),
        displayValue: action.display_name,
        disabled: incompleteTasks.length > 0,
        disabledTooltip:
          incompleteTasks.length > 0 ? (
            <ActionDisabledTooltip tasks={incompleteTasks} />
          ) : undefined,
      } satisfies Option;
    });
  }, [
    caseActions,
    caseData,
    availableTasks,
    getTemplateTask,
    hintedNextActionKey,
  ]);

  const handleChange = useCallback(
    (value: string) => {
      onSelect(value === selectedKey ? null : value);
    },
    [onSelect, selectedKey],
  );

  // Clear selection if the currently selected action becomes disabled because of incomplete tasks
  useEffect(() => {
    if (selectedKey) {
      const selectedOption = caseActionOptions.find(
        (option) => option.key === selectedKey,
      );
      if (selectedOption?.disabled) {
        onSelect(undefined);
      }
    }
  }, [selectedKey, caseActionOptions, onSelect]);

  const buttonRenderer = useCallback(
    (option?: Option) => {
      if (!option) return null;
      return (
        <div className="flex w-full items-center justify-between gap-1">
          <span className="truncate">{option.displayValue}</span>
          {option.key === hintedNextActionKey && (
            <RecommendedBadge dataLoc="recommended-action-badge" />
          )}
        </div>
      );
    },
    [hintedNextActionKey],
  );

  return (
    <Select
      buttonRenderer={buttonRenderer}
      dataLoc="case-action-select"
      disabled={disabled || !caseActionOptions.length}
      options={caseActionOptions}
      placeholder="Select an action"
      value={selectedKey ?? null}
      fullWidth
      onChange={handleChange}
    />
  );
};

const isCaseTemplateAction = (
  caseAction: unknown,
): caseAction is CaseTemplateActionOutput => {
  return !!(caseAction as CaseTemplateActionOutput).display_name;
};

const getFormWindowId = (actionId: string) => `action-form-${actionId}`;

/**
 * Extracts the form values from a given case action, omitting metadata fields.
 */
export const extracActionFormValues = (
  caseAction?: AvailableCaseAction,
): CaseActionFormValues => {
  const defaultFormValues = omit(caseAction?.form ?? {}, [
    "type",
    "meta",
    "relationships",
    "id",
  ]);

  return {
    action_key: caseAction?.action_key,
    ...defaultFormValues,
  };
};
